import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { useDrag, useEdgeResize } from '~/hooks/smart-search';

import styles from './FloatingPanel.module.scss';

const DEFAULT_SIZE = { width: 400, height: 520 };
const SIZE_CONSTRAINTS = { minWidth: 300, maxWidth: 800, minHeight: 300, maxHeight: 720 };
const VIEWPORT_MARGIN = 16;

const getInitialPosition = () => ({
  x: window.innerWidth - DEFAULT_SIZE.width - VIEWPORT_MARGIN,
  y: window.innerHeight - DEFAULT_SIZE.height - VIEWPORT_MARGIN,
});

const FloatingPanel = ({ children }: { children: React.ReactNode }) => {
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [position, setPosition] = useState(getInitialPosition);

  const getSize = useCallback(() => size, [size]);

  const { dragProps, isDragging } = useDrag(position, setPosition, getSize);
  const { resizeProps } = useEdgeResize(size, position, setSize, setPosition, SIZE_CONSTRAINTS);

  const handlePointerDown = (e: React.PointerEvent) => {
    resizeProps.onPointerDown(e);
    dragProps.onPointerDown(e);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) resizeProps.onPointerMove(e);
    dragProps.onPointerMove(e);
  };

  const handlePointerUp = () => {
    resizeProps.onPointerUp();
    dragProps.onPointerUp();
  };

  const handleLostPointerCapture = () => {
    resizeProps.onLostPointerCapture();
    dragProps.onLostPointerCapture();
  };

  return createPortal(
    <div
      className={styles['floating-panel']}
      style={{ left: position.x, top: position.y, width: size.width, height: size.height }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onLostPointerCapture={handleLostPointerCapture}
      onPointerLeave={resizeProps.onPointerLeave}
    >
      {children}
    </div>,
    document.body
  );
};

export default FloatingPanel;
